import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, History, RotateCcw } from 'lucide-react'
import { toast } from 'sonner'

import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import { Checkbox } from '@quebon/ui/components/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldTitle,
} from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@quebon/ui/components/select'
import { Spinner } from '@quebon/ui/components/spinner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@quebon/ui/components/table'
import { Textarea } from '@quebon/ui/components/textarea'

import { handleApiError } from '@/api/client'
import {
  textbookRequestsApi,
  type AdminTextbookRequestView,
  type TextbookRequestStatus,
} from '@/api/textbookRequests'
import {
  TEXTBOOK_STATUS_LABELS,
  textbookRequestBookLabel,
  textbookStatusTouchesOutbound,
} from './textbookRequestStatus'

interface Props {
  request: AdminTextbookRequestView
  onClose: () => void
  onCorrected: () => void
}

const ACTION_LABELS = {
  CREATED: '요청 생성',
  STATUS_CHANGE: '상태 변경',
  HAPPYDAY_DISPATCH: '해피데이 요청',
  HAPPYDAY_RESULT: '해피데이 응답',
  CORRECTION: '상태 정정',
} as const

export function TextbookStatusHistoryDialog({ request, onClose, onCorrected }: Props) {
  const queryClient = useQueryClient()
  const queryKey = ['admin', 'textbook-requests', request.id, 'status-history'] as const
  const [reason, setReason] = useState('')
  const [externalConfirmed, setExternalConfirmed] = useState(false)
  const [externalReference, setExternalReference] = useState('')
  const [legacyTargetStatus, setLegacyTargetStatus] = useState<TextbookRequestStatus | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const historyQuery = useQuery({
    queryKey,
    queryFn: () => textbookRequestsApi.statusHistory(request.id),
  })
  const history = historyQuery.data
  const targetStatus = history?.correctionTargetStatus ?? legacyTargetStatus
  const requiresExternalConfirmation = history && targetStatus
    ? textbookStatusTouchesOutbound(history.currentStatus, targetStatus)
    : false
  const reasonInvalid = submitted && !reason.trim()
  const targetInvalid = submitted && !targetStatus
  const externalInvalid = submitted && requiresExternalConfirmation && !externalConfirmed

  const correctionMutation = useMutation({
    mutationFn: () => {
      if (!history || !targetStatus) throw new Error('Correction target is unavailable')
      return textbookRequestsApi.correctLastStatus(request.id, {
        expectedStatus: history.currentStatus,
        attemptId: crypto.randomUUID(),
        reason: reason.trim(),
        externalConfirmed,
        externalReference: externalReference.trim() || null,
        legacyTargetStatus: history.correctionTargetStatus ? null : targetStatus,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      toast.success('마지막 상태 전이를 정정했습니다.')
      onCorrected()
      onClose()
    },
    onError: (error) => toast.error(handleApiError(error)),
  })

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitted(true)
    if (!reason.trim() || !targetStatus || externalInvalid) return
    correctionMutation.mutate()
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !correctionMutation.isPending) onClose() }}>
      <DialogContent className="sm:max-w-3xl" showCloseButton={!correctionMutation.isPending}>
        <form className="contents" onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>교재 요청 상태 이력</DialogTitle>
            <DialogDescription>
              {request.organizationName ?? '센터'} · {request.learnerName ?? '학생'} · {textbookRequestBookLabel(request)}
            </DialogDescription>
          </DialogHeader>

          {historyQuery.isLoading ? (
            <div className="flex min-h-32 items-center justify-center"><Spinner /></div>
          ) : historyQuery.isError || !history ? (
            <Alert variant="destructive">
              <AlertTriangle />
              <AlertTitle>상태 이력을 불러오지 못했습니다.</AlertTitle>
              <AlertDescription>
                <Button type="button" variant="outline" size="sm" onClick={() => historyQuery.refetch()}>다시 시도</Button>
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {history.events.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>시각</TableHead>
                      <TableHead>상태</TableHead>
                      <TableHead>경로</TableHead>
                      <TableHead>처리자</TableHead>
                      <TableHead>사유</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.events.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell className="tabular-nums">{new Date(event.createdAt).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}</TableCell>
                        <TableCell>
                          {event.fromStatus ? TEXTBOOK_STATUS_LABELS[event.fromStatus] : '없음'} → {TEXTBOOK_STATUS_LABELS[event.toStatus]}
                        </TableCell>
                        <TableCell><Badge variant="secondary">{ACTION_LABELS[event.action]} · {event.source}</Badge></TableCell>
                        <TableCell>{event.actorName ?? (event.actorUserId ? `#${event.actorUserId}` : '시스템')}</TableCell>
                        <TableCell className="max-w-56 whitespace-normal">{event.reason ?? '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Alert>
                  <History />
                  <AlertTitle>도입 전 요청이라 상태 이벤트가 없습니다.</AlertTitle>
                  <AlertDescription>Superadmin 검토형 정정만 가능하며 복원할 상태를 직접 선택해야 합니다.</AlertDescription>
                </Alert>
              )}

              {history.correctionTargetStatus || history.legacyTargetStatuses.length > 0 ? (
                <FieldGroup>
                  {history.correctionTargetStatus ? (
                    <Alert>
                      <RotateCcw />
                      <AlertTitle>마지막 전이 정정</AlertTitle>
                      <AlertDescription>
                        {TEXTBOOK_STATUS_LABELS[history.currentStatus]} → {TEXTBOOK_STATUS_LABELS[history.correctionTargetStatus]}
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Field data-invalid={targetInvalid || undefined}>
                      <FieldLabel htmlFor="legacy-correction-target">검토 후 복원 상태 *</FieldLabel>
                      <Select
                        items={history.legacyTargetStatuses.map((status) => ({ value: status, label: TEXTBOOK_STATUS_LABELS[status] }))}
                        value={legacyTargetStatus}
                        onValueChange={(value) => setLegacyTargetStatus(value as TextbookRequestStatus | null)}
                      >
                        <SelectTrigger id="legacy-correction-target" aria-invalid={targetInvalid || undefined}>
                          <SelectValue placeholder="복원할 상태를 선택하세요" />
                        </SelectTrigger>
                        <SelectContent alignItemWithTrigger={false}>
                          <SelectGroup>
                            {history.legacyTargetStatuses.map((status) => (
                              <SelectItem key={status} value={status}>{TEXTBOOK_STATUS_LABELS[status]}</SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      {targetInvalid ? <FieldError>복원할 상태를 선택해 주세요.</FieldError> : null}
                    </Field>
                  )}

                  <Field data-invalid={reasonInvalid || undefined}>
                    <FieldLabel htmlFor="textbook-correction-reason">정정 사유 *</FieldLabel>
                    <Textarea
                      id="textbook-correction-reason"
                      value={reason}
                      maxLength={1000}
                      disabled={correctionMutation.isPending}
                      aria-invalid={reasonInvalid || undefined}
                      onChange={(event) => setReason(event.target.value)}
                    />
                    {reasonInvalid ? <FieldError>정정 사유를 입력해 주세요.</FieldError> : null}
                  </Field>

                  {requiresExternalConfirmation ? (
                    <>
                      <Field orientation="horizontal" data-invalid={externalInvalid || undefined}>
                        <Checkbox
                          id="correction-external-confirmed"
                          checked={externalConfirmed}
                          disabled={correctionMutation.isPending}
                          aria-invalid={externalInvalid || undefined}
                          onCheckedChange={setExternalConfirmed}
                        />
                        <FieldContent>
                          <FieldLabel htmlFor="correction-external-confirmed">
                            <FieldTitle>해피데이의 실제 상태와 정정 결과가 일치함을 확인했습니다 *</FieldTitle>
                          </FieldLabel>
                          <FieldDescription>깨봉 상태 정정은 해피데이 상태를 변경하지 않습니다.</FieldDescription>
                          {externalInvalid ? <FieldError>외부 상태 확인이 필요합니다.</FieldError> : null}
                        </FieldContent>
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="correction-external-reference">외부 확인 근거</FieldLabel>
                        <Input
                          id="correction-external-reference"
                          value={externalReference}
                          maxLength={200}
                          disabled={correctionMutation.isPending}
                          onChange={(event) => setExternalReference(event.target.value)}
                        />
                      </Field>
                    </>
                  ) : null}
                </FieldGroup>
              ) : (
                <Alert>
                  <History />
                  <AlertTitle>현재 정정 가능한 상태 전이가 없습니다.</AlertTitle>
                  <AlertDescription>요청 생성 자체 또는 지원하지 않는 상태는 직전 전이 정정 대상이 아닙니다.</AlertDescription>
                </Alert>
              )}
            </>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" disabled={correctionMutation.isPending} onClick={onClose}>닫기</Button>
            {history && (history.correctionTargetStatus || history.legacyTargetStatuses.length > 0) ? (
              <Button type="submit" variant="destructive" disabled={correctionMutation.isPending}>
                {correctionMutation.isPending ? <Spinner data-icon="inline-start" /> : <RotateCcw data-icon="inline-start" />}
                {correctionMutation.isPending ? '정정 중' : '마지막 전이 정정'}
              </Button>
            ) : null}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
